import { getMutator, useUpdatables } from "./useUpdatable";
import { MarketSchedules } from "../../openapi-typescript/common/market_schedules";
import { UserScopes } from "../../openapi-typescript/common/user_scopes";
import { buildQuerySelect, keysToSnake } from "../global";
import { MarketScheduleAlert } from "../../openapi-typescript/common/market_schedule_alerts";
import { checkIsMarketOpen } from "../../modules/markets/shared/marketSchedulesHelper";

type UseMarketSchedulesProps = {
  scope?: UserScopes;
  isActive?: boolean;
  enabled?: boolean;
};

export const transformMarketsToIncludeMarketStatus = (
  markets: MarketSchedules[]
): MarketSchedules[] => {
  return markets.map(
    (market): MarketSchedules => ({
      ...market,
      isMarketOpen: checkIsMarketOpen(market),
    })
  );
};

const buildParams = ({ scope, isActive }: UseMarketSchedulesProps) => {
  return keysToSnake({
    ...(isActive && { isActive: "eq.true" }),
    ...(scope && { scope: `eq.${scope}` }),
  });
};

export const useMarketSchedules = (args?: UseMarketSchedulesProps) => {
  const res = useUpdatables<MarketSchedules>({
    get: [
      "getMarketSchedules",
      {
        select: buildQuerySelect({
          id: "id",
          scope: "scope",
          marketName: "market_name",
          pageUrl: "page_url",
          categories: "categories",
          openTime: "open_time",
          closeTime: "close_time",
          calendar: "calendars(*,holidays:holidays(*))",
          timezone: "timezone",
          isActive: "is_active",
          marketScheduleAlerts: `market_schedule_alerts(${buildQuerySelect({
            id: "id",
            title: "title",
            isActive: "is_active",
            eventType: "event_type",
            description: "description",
            reminderInterval: "reminder_interval",
          })})`,
        }),
        ...(args && buildParams(args)),
      },
      args?.enabled === false,
    ],
    update: (newItem, oldItem) => ({
      queryKey: [
        "editMarketSchedules",
        {
          action: "editMarketSchedules",
          body: JSON.stringify(newItem),
          params: { id: `eq.${oldItem.id}` },
          enabled: true,
        },
      ],
    }),
    transformToRichTypes: (record) => record,
    invalidateQueries: ["getMarketSchedules"],
  });

  const updateMarketScheduleAlert = async (
    id: MarketScheduleAlert["id"],
    body: Partial<MarketScheduleAlert>
  ) => {
    return await getMutator(
      {
        queryKey: [
          "updateMarketScheduleAlerts",
          {
            action: "updateMarketScheduleAlerts",
            params: { id: `eq.${id}` },
            body: JSON.stringify(body),
            enabled: true,
          },
        ],
      },
      ["getMarketSchedules"]
    );
  };
  return {
    ...res,
    updateMarketScheduleAlert,
  };
};
